/**
 * TagConditionControls — the toggle shown when a row authors a `tag` node:
 * [ is | isn't ]. The tag sits to its left, so the row reads as a statement —
 * "TRASH is" / "TRASH isn't" — rather than as a verb with no subject.
 * The tag's color + emoji live on the Field <select>
 * itself (RuleConditionRow), so the row reads as one clean colored dropdown.
 * `negate` flips membership. No threshold.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { RuleNode } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

type TagNode = Extract<RuleNode, { kind: 'tag' }>;

export interface TagConditionControlsProps {
  node: TagNode;
  onChange: (next: RuleNode) => void;
}

const OPTIONS: { negate: boolean; label: string }[] = [
  { negate: false, label: 'is' },
  { negate: true, label: "isn't" },
];

const TagConditionControls: FC<TagConditionControlsProps> = ({ node, onChange }) => (
  <div
    role="radiogroup"
    aria-label="Tag membership"
    className="flex items-center gap-px rounded border border-border bg-muted/20 p-px"
  >
    {OPTIONS.map((o) => {
      const active = Boolean(node.negate) === o.negate;
      return (
        <button
          key={o.label}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange({ ...node, negate: o.negate })}
          className={cn(
            'h-7 rounded-sm px-2 text-[11px] font-semibold transition-colors',
            active
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

export default TagConditionControls;
